<script>
import { getContrastingTextColor } from '@chatwoot/utils';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { Chrome } from '@lk77/vue3-color';

const THEME_SWATCHES = [
  // LiveChat-like palette (mirrors /root/livechatsetting vibe)
  '#1c1c1c',
  '#0059e1',
  '#0246ff',
  '#05acff',
  '#00d969',
  '#b7e000',
  '#ffd000',
  '#ff3f00',
  '#ff2e2e',
  '#ff4fc3',
  '#994bf4',
];

// Gradient + solid presets for midnight bubble colors
const MIDNIGHT_BUBBLE_PRESETS = [
  { label: 'Orange', value: '#ff8731', preview: '#ff8731' },
  {
    label: 'Purple',
    value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    preview: '#667eea',
  },
  {
    label: 'Teal',
    value: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    preview: '#11998e',
  },
  {
    label: 'Pink',
    value: 'linear-gradient(135deg, #fc5c7d 0%, #6a3093 100%)',
    preview: '#fc5c7d',
  },
  {
    label: 'Sunset',
    value: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)',
    preview: '#f7971e',
  },
  {
    label: 'Ocean',
    value: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
    preview: '#2193b0',
  },
  {
    label: 'Fire',
    value: 'linear-gradient(135deg, #ff4500 0%, #ffb700 100%)',
    preview: '#ff4500',
  },
  {
    label: 'Indigo',
    value: 'linear-gradient(135deg, #141e30 0%, #243b55 100%)',
    preview: '#243b55',
  },
  { label: 'Blue', value: '#0059e1', preview: '#0059e1' },
  { label: 'Green', value: '#00d969', preview: '#00d969' },
  { label: 'Red', value: '#ff2e2e', preview: '#ff2e2e' },
  {
    label: 'Magenta',
    value: 'linear-gradient(135deg, #f953c6 0%, #b91d73 100%)',
    preview: '#f953c6',
  },
];

const MIDNIGHT_THEME_TEMPLATES = {
  dark: {
    midnightVariant: 'dark',
    accent: '#ff8731',
    background: '#111111',
    surface: '#252525',
    border: '#333333',
    text: '#ffffff',
    mutedText: '#b3b3b3',
    input: '#242424',
    homeCard: '#252525',
    // Bubble colors — default to orange
    userBubbleBg: '#ff8731',
    userBubbleText: '#ffffff',
    agentBubbleBg: '',
    agentBubbleText: '',
  },
  light: {
    midnightVariant: 'light',
    // Light midnight should resemble the LiveChat light widget (screenshot)
    accent: '#0059e1',
    background: '#f6f6f7',
    surface: '#ffffff',
    border: '#e5e7eb',
    text: '#111111',
    mutedText: '#6b7280',
    input: '#ffffff',
    homeCard: '#ffffff',
    // Bubble colors — default to blue
    userBubbleBg: '#0059e1',
    userBubbleText: '#ffffff',
    agentBubbleBg: '#ffffff',
    agentBubbleText: '#111111',
  },
};

const CLASSIC_DEFAULT_PRIMARY = '#0059e1';
const CLASSIC_THEME_TEMPLATES = {
  light: {
    chatBackground: '#f6f6f7',
    customerBubbleText: '#ffffff',
    agentBubbleBg: '#ffffff',
    agentBubbleText: '#111111',
    systemMessageColor: '#707070',
  },
  dark: {
    chatBackground: '#17171a',
    customerBubbleText: '#ffffff',
    agentBubbleBg: '#252528',
    agentBubbleText: '#edeef0',
    systemMessageColor: '#b0b4ba',
  },
};

// Build script tags at runtime to avoid any "</" sequence in source text
// (Vue SFC tokeniser exits raw-text mode on "</" even inside <script> blocks)
const SCRIPT_OPEN = '\x3cscript\x3e';
const SCRIPT_CLOSE = '\x3c\x2fscript\x3e';

export default {
  components: {
    NextButton,
    ConfirmationModal,
    Chrome,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // Existing widget fields (preserve backend contract)
      color: '#0059e1',
      websiteName: '',
      welcomeHeading: '',
      welcomeTagline: '',
      replyTime: 'in_a_few_minutes',
      avatarFile: null,
      avatarUrl: '',
      announcementEnabled: false,
      announcementText: '',
      announcementUrl: '',
      announcementBgColor: '#242529',
      announcementTextColor: '#ffffff',
      skipStartConversation: false,
      widgetLanguage: 'en',
      widgetBubblePosition: 'right',
      widgetBubbleLauncherTitle: 'Chat with us',
      widgetBubbleType: 'standard',

      // Builder UI states
      activeSection: 'appearance',
      minimizedWindowStyle: 'bubble',
      widgetThemePreset: 'classic', // classic | midnight
      themeMode: 'light',
      midnightThemeConfig: { ...MIDNIGHT_THEME_TEMPLATES.dark },
      midnightBubblePresets: MIDNIGHT_BUBBLE_PRESETS,
      colorSettingsMode: 'theme', // "Theme color" | "More color settings"
      minimizedBubbleColor: '#0059e1',
      minimizedIconColor: '#ffffff',
      minimizedIconImageUrl: '',
      maximizedChatBackgroundColor: '#f6f6f7',
      maximizedChatBackgroundOpacity: '100%',
      topSafeAreaColor: '',
      bottomSafeAreaColor: '',
      maximizedPrimaryColor: '#0059e1', // Primary (widget_color)
      customerBubbleBgColor: '#0059e1',
      customerBubbleTextColor: '#ffffff',
      agentBubbleBgColor: '#ffffff',
      agentBubbleTextColor: '#111111',
      systemMessageColor: '#707070',
      sideSpacing: 0,
      bottomSpacing: 0,
      mobileSideSpacing: 0,
      mobileBottomSpacing: 0,
      visibilityMode: 'always_visible',
      showMobileWidget: true,
      mobileMode: 'same_as_desktop',
      showLogo: true,
      showAgentPhoto: true,
      enableSoundForCustomers: true,
      enableAgentRating: true,
      enableChatTranscript: true,
      whiteLabelLocked: true,
      themeSwatches: THEME_SWATCHES,
      // Dark-mode tracking (body.classList.contains('dark'))
      isDark: document.body.classList.contains('dark'),
      darkObserver: null,
      // Preview iframe: stored HTML to avoid reactive reloads
      previewDocHtml: '',
      // Color picker popover state
      isColorPopoverOpen: false,
      activeColorPickerTarget: null,
      activeColorPickerValue: '#000000',
      colorPopoverTop: 0,
      colorPopoverLeft: 0,
      pendingMidnightVariant: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
    storageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
    midnightVariant() {
      return this.midnightThemeConfig?.midnightVariant === 'light'
        ? 'light'
        : 'dark';
    },
    resolvedDarkMode() {
      return this.widgetThemePreset === 'midnight'
        ? this.midnightVariant
        : this.themeMode;
    },
    midnightVariantConfirmTitle() {
      const v = this.pendingMidnightVariant;
      if (!v) return 'Switch Midnight template';
      return v === 'light'
        ? 'Switch to Midnight Light?'
        : 'Switch to Midnight Dark?';
    },
    midnightVariantConfirmDescription() {
      const v = this.pendingMidnightVariant;
      if (!v) return 'This will reset the Midnight custom colors to defaults.';
      const target = v === 'light' ? 'Light' : 'Dark';
      return `This will reset the Midnight custom colors below to the default ${target} template.`;
    },
    midnightVariantConfirmLabel() {
      const v = this.pendingMidnightVariant;
      if (!v) return 'Switch';
      return v === 'light' ? 'Switch to Light' : 'Switch to Dark';
    },
    widgetScript() {
      const script = this.inbox.web_widget_script || '';
      if (!script || script.length < 13) return script;
      const isMidnight = this.widgetThemePreset === 'midnight';
      const options = {
        position: this.widgetBubblePosition,
        type: this.widgetBubbleType,
        launcherTitle: this.widgetBubbleLauncherTitle,
        darkMode: this.resolvedDarkMode,
        themePreset: this.widgetThemePreset,
        sideSpacing: this.sideSpacing || 0,
        bottomSpacing: this.bottomSpacing || 0,
        showMobileWidget: this.showMobileWidget,
        mobileMode: this.mobileMode,
        mobileSideSpacing: this.mobileSideSpacing || 0,
        mobileBottomSpacing: this.mobileBottomSpacing || 0,
        bubbleColor: this.minimizedBubbleColor || '#0059e1',
        bubbleIconColor: this.minimizedIconColor || undefined,
        bubbleIconImageUrl: this.minimizedIconImageUrl || undefined,
        themeConfig: isMidnight
          ? {
              accent: this.midnightThemeConfig.accent,
              ...this.midnightThemeConfig,
              topSafeAreaColor:
                this.topSafeAreaColor || this.midnightThemeConfig.background,
              bottomSafeAreaColor:
                this.bottomSafeAreaColor || this.midnightThemeConfig.background,
              position: this.widgetBubblePosition,
              globalShowLogo: this.showLogo,
              showAgentPhoto: this.showAgentPhoto,
              enableSoundForCustomers: this.enableSoundForCustomers,
              enableAgentRating: this.enableAgentRating,
              enableChatTranscript: this.enableChatTranscript,
              announcementEnabled: this.announcementEnabled,
              sideSpacing: this.sideSpacing || 0,
              bottomSpacing: this.bottomSpacing || 0,
              showMobileWidget: this.showMobileWidget,
              mobileMode: this.mobileMode,
              mobileSideSpacing: this.mobileSideSpacing || 0,
              mobileBottomSpacing: this.mobileBottomSpacing || 0,
            }
          : undefined,
        announcementText: this.announcementText,
        announcementUrl: this.announcementUrl,
        announcementBgColor: this.announcementBgColor,
        announcementTextColor: this.announcementTextColor,
        hideMessageBubble: this.visibilityMode !== 'always_visible',
      };
      return (
        script.substring(0, 13) +
        this.$t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_SETTINGS', {
          options: JSON.stringify(options),
        }) +
        script.substring(13)
      );
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    liveChatwootSettings() {
      const hideMessageBubble = this.visibilityMode !== 'always_visible';
      const isMidnight = this.widgetThemePreset === 'midnight';
      const primaryColor =
        this.maximizedPrimaryColor || this.color || '#0059e1';
      return {
        locale: this.widgetLanguage || this.inbox.widget_language || 'en',
        position: this.widgetBubblePosition,
        type: this.widgetBubbleType,
        // Preview must be able to render unsaved theme changes from host settings.
        allowThemeOverride: true,
        launcherTitle:
          this.widgetBubbleType === 'expanded_bubble'
            ? this.widgetBubbleLauncherTitle
            : '',
        darkMode: this.resolvedDarkMode,
        themePreset: this.widgetThemePreset,
        sideSpacing: this.sideSpacing || 0,
        bottomSpacing: this.bottomSpacing || 0,
        showMobileWidget: this.showMobileWidget,
        mobileMode: this.mobileMode,
        mobileSideSpacing: this.mobileSideSpacing || 0,
        mobileBottomSpacing: this.mobileBottomSpacing || 0,
        widgetStyle: 'rounded',
        // Primary UI color (header/buttons/etc inside widget)
        widgetColor: primaryColor,
        // Launcher bubble (SDK – outside iframe)
        bubbleColor: this.minimizedBubbleColor || '#0059e1',
        bubbleIconColor: this.minimizedIconColor || '#ffffff',
        bubbleIconImageUrl: this.minimizedIconImageUrl || undefined,
        // Back-compat keys used by preview style injection
        iconColor: this.minimizedIconColor || '#ffffff',
        themeConfig: isMidnight
          ? {
              accent: this.midnightThemeConfig.accent,
              ...this.midnightThemeConfig,
              topSafeAreaColor:
                this.topSafeAreaColor || this.midnightThemeConfig.background,
              bottomSafeAreaColor:
                this.bottomSafeAreaColor || this.midnightThemeConfig.background,
              position: this.widgetBubblePosition,
              globalShowLogo: this.showLogo,
              showAgentPhoto: this.showAgentPhoto,
              enableSoundForCustomers: this.enableSoundForCustomers,
              enableAgentRating: this.enableAgentRating,
              enableChatTranscript: this.enableChatTranscript,
              announcementEnabled: this.announcementEnabled,
              sideSpacing: this.sideSpacing || 0,
              bottomSpacing: this.bottomSpacing || 0,
              showMobileWidget: this.showMobileWidget,
              mobileMode: this.mobileMode,
              mobileSideSpacing: this.mobileSideSpacing || 0,
              mobileBottomSpacing: this.mobileBottomSpacing || 0,
            }
          : {
              // Classic theme config – bubble / message colors
              darkMode: this.resolvedDarkMode,
              widgetColor: primaryColor,
              position: this.widgetBubblePosition,
              customerBubbleBg: this.customerBubbleBgColor || primaryColor,
              customerBubbleText: this.customerBubbleTextColor || '#ffffff',
              agentBubbleBg: this.agentBubbleBgColor || '#ffffff',
              agentBubbleText: this.agentBubbleTextColor || '#111111',
              systemMessageColor: this.systemMessageColor || '#707070',
              chatBackground: this.maximizedChatBackgroundColor || '#f6f6f7',
              topSafeAreaColor:
                this.topSafeAreaColor ||
                this.maximizedChatBackgroundColor ||
                '#f6f6f7',
              bottomSafeAreaColor:
                this.bottomSafeAreaColor ||
                this.maximizedChatBackgroundColor ||
                '#f6f6f7',
              globalShowLogo: this.showLogo,
              showAgentPhoto: this.showAgentPhoto,
              enableSoundForCustomers: this.enableSoundForCustomers,
              enableAgentRating: this.enableAgentRating,
              enableChatTranscript: this.enableChatTranscript,
              sideSpacing: this.sideSpacing || 0,
              bottomSpacing: this.bottomSpacing || 0,
              showMobileWidget: this.showMobileWidget,
              mobileMode: this.mobileMode,
              mobileSideSpacing: this.mobileSideSpacing || 0,
              mobileBottomSpacing: this.mobileBottomSpacing || 0,
            },
        announcementText: this.announcementText,
        announcementUrl: this.announcementUrl,
        announcementBgColor: this.announcementBgColor,
        announcementTextColor: this.announcementTextColor,
        hideMessageBubble,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        welcomeTitle: this.welcomeHeading || '',
        welcomeDescription: this.welcomeTagline || '',
        initialRoute: 'messages',
      };
    },
  },
  watch: {
    widgetThemePreset(preset, previousPreset) {
      if (preset === 'midnight') {
        // Ensure backward-compat for older saved configs
        if (!this.midnightThemeConfig?.midnightVariant) {
          this.midnightThemeConfig = { ...MIDNIGHT_THEME_TEMPLATES.dark };
        }
        if (!this.color || this.color === CLASSIC_DEFAULT_PRIMARY) {
          this.selectThemeColor(this.midnightThemeConfig.accent);
        }
      }
      if (preset === 'classic' && previousPreset === 'midnight') {
        const currentColor = (this.color || '').toLowerCase();
        const inheritedMidnightAccents = [
          this.midnightThemeConfig?.accent,
          MIDNIGHT_THEME_TEMPLATES.dark.accent,
          MIDNIGHT_THEME_TEMPLATES.light.accent,
        ]
          .filter(Boolean)
          .map(value => value.toLowerCase());

        if (!currentColor || inheritedMidnightAccents.includes(currentColor)) {
          this.applyThemeSwatch(CLASSIC_DEFAULT_PRIMARY);
        }
        this.syncClassicThemeDefaults(this.themeMode);
      }
      // Rebuild on theme switch (different dark mode / preset needs fresh init)
      this.rebuildPreviewDoc();
    },
    themeMode(nextMode) {
      this.syncClassicThemeDefaults(nextMode);
      this.pushSettingsToPreview();
    },
    color() {
      if (this.widgetThemePreset === 'midnight') {
        this.midnightThemeConfig = {
          ...this.midnightThemeConfig,
          accent: this.color || this.midnightThemeConfig.accent,
        };
      }
      // Push only – never rebuild (which would close/reset the preview widget)
      this.pushSettingsToPreview();
    },
    maximizedPrimaryColor(next) {
      // Keep backend widget_color in sync with "Primary color"
      if (next && next !== this.color) {
        this.color = next;
      } else {
        this.pushSettingsToPreview();
      }
    },
    widgetBubbleType() {
      // Bubble type (bar vs bubble) is structural in the SDK → requires re-init
      this.rebuildPreviewDoc();
    },
    'midnightThemeConfig.accent'(next) {
      if (this.widgetThemePreset !== 'midnight' || !next) return;
      if (next !== this.color) this.applyThemeSwatch(next);
    },
    midnightThemeConfig: {
      deep: true,
      handler() {
        if (this.widgetThemePreset === 'midnight') {
          this.pushSettingsToPreview();
        }
      },
    },
    announcementText() {
      this.pushSettingsToPreview();
    },
    announcementEnabled() {
      this.pushSettingsToPreview();
    },
    announcementUrl() {
      this.pushSettingsToPreview();
    },
    announcementBgColor() {
      this.pushSettingsToPreview();
    },
    announcementTextColor() {
      this.pushSettingsToPreview();
    },
    maximizedChatBackgroundColor() {
      this.pushSettingsToPreview();
    },
    customerBubbleBgColor() {
      this.pushSettingsToPreview();
    },
    customerBubbleTextColor() {
      this.pushSettingsToPreview();
    },
    agentBubbleBgColor() {
      this.pushSettingsToPreview();
    },
    agentBubbleTextColor() {
      this.pushSettingsToPreview();
    },
    systemMessageColor() {
      this.pushSettingsToPreview();
    },
    topSafeAreaColor() {
      this.pushSettingsToPreview();
    },
    bottomSafeAreaColor() {
      this.pushSettingsToPreview();
    },
    widgetBubblePosition() {
      // Position affects host shell classes (holder/bubble); rebuild for accurate preview.
      this.rebuildPreviewDoc();
    },
    sideSpacing() {
      this.pushSettingsToPreview();
    },
    bottomSpacing() {
      this.pushSettingsToPreview();
    },
    showMobileWidget() {
      this.pushSettingsToPreview();
    },
    mobileMode() {
      this.pushSettingsToPreview();
    },
    mobileSideSpacing() {
      this.pushSettingsToPreview();
    },
    mobileBottomSpacing() {
      this.pushSettingsToPreview();
    },
    showLogo() {
      this.pushSettingsToPreview();
    },
    showAgentPhoto() {
      this.pushSettingsToPreview();
    },
    enableSoundForCustomers() {
      this.pushSettingsToPreview();
    },
    enableAgentRating() {
      this.pushSettingsToPreview();
    },
    enableChatTranscript() {
      this.pushSettingsToPreview();
    },
  },
  mounted() {
    this.setDefaults();
    this.$nextTick(() => {
      this.previewDocHtml = this.buildPreviewDoc();
    });
    document.addEventListener('click', this.handleGlobalClick);
    this.darkObserver = new MutationObserver(() => {
      this.isDark = document.body.classList.contains('dark');
    });
    this.darkObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick);
    this.darkObserver?.disconnect();
  },
  methods: {
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.widgetThemePreset === 'midnight';
      const bg = isMidnight
        ? this.midnightThemeConfig?.background || '#111111'
        : '#ffffff';
      const accent = this.minimizedBubbleColor || this.color || '#0059e1';
      const iconColor = this.minimizedIconColor || '#ffffff';
      const side = Math.max(0, Number(this.sideSpacing) || 0);
      const bottom = Math.max(0, Number(this.bottomSpacing) || 0);
      const mSide = Math.max(0, Number(this.mobileSideSpacing) || 0);
      const mBottom = Math.max(0, Number(this.mobileBottomSpacing) || 0);
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);
      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style id="wlab-bubble-style">` +
        `:root{` +
        `--woot-widget-side-spacing-extra:${side}px;` +
        `--woot-widget-bottom-spacing-extra:${bottom}px;` +
        `--woot-widget-side-spacing-extra-mobile:${mSide}px;` +
        `--woot-widget-bottom-spacing-extra-mobile:${mBottom}px;` +
        `}` +
        `html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot-widget-bubble--launcher{background:${accent} !important;color:${iconColor} !important;}` +
        `.woot-widget-bubble--launcher-close{background:${accent} !important;}` +
        `.woot-widget-holder:not(.woot--hide){box-shadow:0 10px 24px rgba(15,23,42,0.18) !important;}` +
        /* Bubble starts invisible; polling (below) restores it when widget is closed */
        `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}` +
        `</style>` +
        `</head><body>` +
        SCRIPT_OPEN +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        /* Update bubble button CSS without reloading */
        `var st=document.getElementById('wlab-bubble-style');` +
        `if(st){` +
        `var ac=(d.s.bubbleColor)||'${accent}';var ic=(d.s.bubbleIconColor||d.s.iconColor)||'${iconColor}';var iu=(d.s.bubbleIconImageUrl)||'';` +
        `var ss=Math.max(0,Number(d.s.sideSpacing||0));var bs=Math.max(0,Number(d.s.bottomSpacing||0));` +
        `var mss=Math.max(0,Number(d.s.mobileSideSpacing||0));var mbs=Math.max(0,Number(d.s.mobileBottomSpacing||0));` +
        `var bgc=(d.s.themePreset==='midnight')?((d.s.themeConfig&&d.s.themeConfig.background)||(d.s.darkMode==='light'?'#f6f6f7':'#111111')):'${bg}';` +
        `st.textContent='html,body{margin:0;width:100%;height:100%;background:'+bgc+';overflow:hidden;}'+` +
        `'body{position:fixed;inset:0;overflow:hidden;}'+` +
        `':root{--woot-widget-side-spacing-extra:'+ss+'px;--woot-widget-bottom-spacing-extra:'+bs+'px;--woot-widget-side-spacing-extra-mobile:'+mss+'px;--woot-widget-bottom-spacing-extra-mobile:'+mbs+'px;}'+` +
        `'.woot-widget-bubble--launcher{background:'+ac+' !important;color:'+ic+' !important;}'+` +
        `'.woot-widget-bubble--launcher-close{background:'+ac+' !important;}'+` +
        `'.woot-widget-holder:not(.woot--hide){box-shadow:0 10px 24px rgba(15,23,42,0.18) !important;}';` +
        `}` +
        `var bubble=document.querySelector('.woot-widget-bubble');` +
        `if(bubble){` +
        `var img=bubble.querySelector('.woot-widget-bubble__custom-icon');` +
        `var svg=bubble.querySelector('#woot-widget-bubble-icon');` +
        `if(iu){` +
        `if(!img){img=document.createElement('img');img.className='woot-widget-bubble__custom-icon';img.alt='Open chat window';img.draggable=false;if(svg)svg.remove();bubble.insertBefore(img,bubble.firstChild);}` +
        `img.src=iu;` +
        `}else if(img){img.remove();}` +
        `}` +
        /* Forward to widget iframe */
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{` +
        `locale:(d.s&&d.s.locale)||(window.$chatwoot&&window.$chatwoot.locale)||'en',` +
        `showUnreadMessagesDialog:true,` +
        `isPreviewUpdate:true` +
        `})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){` +
        `var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        /* Auto-open the widget and keep it open after init */
        `g.onload=function(){` +
        `window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        /* Poll widget-holder class to show/hide bubble when widget is opened/closed */
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);` +
        `};` +
        `})(document,"script");` +
        SCRIPT_CLOSE +
        `</body></html>`
      );
    },
    rebuildPreviewDoc() {
      this.previewDocHtml = this.buildPreviewDoc();
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe || !iframe.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (e) {
        // iframe not ready yet; will pick up settings on next reload
      }
    },
    setDefaults() {
      const {
        name,
        welcome_title,
        welcome_tagline,
        widget_color,
        reply_time,
        avatar_url,
        announcement_text,
        announcement_url,
        announcement_bg_color,
        announcement_text_color,
        widget_theme_preset,
        widget_language,
      } = this.inbox;

      this.websiteName = name || '';
      this.welcomeHeading = welcome_title || '';
      this.welcomeTagline = welcome_tagline || '';
      this.color = widget_color || this.color;
      this.minimizedBubbleColor = '#0059e1';
      this.maximizedPrimaryColor = this.color;
      this.customerBubbleBgColor = this.color;
      this.replyTime = reply_time || this.replyTime;
      this.avatarUrl = avatar_url || '';
      this.announcementEnabled =
        typeof this.inbox.widget_theme_config?.announcementEnabled === 'boolean'
          ? this.inbox.widget_theme_config.announcementEnabled
          : !!announcement_text;
      this.announcementText = announcement_text || '';
      this.announcementUrl = announcement_url || '';
      this.announcementBgColor = announcement_bg_color || '#242529';
      this.announcementTextColor = announcement_text_color || '#ffffff';
      this.widgetThemePreset = widget_theme_preset || this.widgetThemePreset;
      this.skipStartConversation = this.inbox.skip_start_conversation || false;
      this.widgetLanguage = widget_language || 'en';
      this.showLogo = !!this.avatarUrl;

      // Persisted (DB) config for host + widget tweaks
      const persistedCfg = this.inbox.widget_theme_config || {};
      if (
        persistedCfg.darkMode &&
        ['light', 'dark'].includes(persistedCfg.darkMode)
      ) {
        this.themeMode = persistedCfg.darkMode;
      }
      this.widgetBubblePosition =
        persistedCfg.position || this.widgetBubblePosition;
      this.sideSpacing =
        Number(persistedCfg.sideSpacing ?? this.sideSpacing) || 0;
      this.bottomSpacing =
        Number(persistedCfg.bottomSpacing ?? this.bottomSpacing) || 0;
      this.showMobileWidget =
        persistedCfg.showMobileWidget ?? this.showMobileWidget;
      this.mobileMode = persistedCfg.mobileMode || this.mobileMode;
      this.mobileSideSpacing =
        Number(persistedCfg.mobileSideSpacing ?? this.mobileSideSpacing) || 0;
      this.mobileBottomSpacing =
        Number(persistedCfg.mobileBottomSpacing ?? this.mobileBottomSpacing) ||
        0;
      this.showAgentPhoto = persistedCfg.showAgentPhoto ?? this.showAgentPhoto;
      this.enableSoundForCustomers =
        persistedCfg.enableSoundForCustomers ?? this.enableSoundForCustomers;
      this.enableAgentRating =
        persistedCfg.enableAgentRating ?? this.enableAgentRating;
      this.enableChatTranscript =
        persistedCfg.enableChatTranscript ?? this.enableChatTranscript;
      this.showLogo = persistedCfg.globalShowLogo ?? this.showLogo;
      this.minimizedBubbleColor =
        persistedCfg.bubbleColor || this.minimizedBubbleColor;
      this.minimizedIconColor =
        persistedCfg.bubbleIconColor || this.minimizedIconColor;
      this.minimizedIconImageUrl =
        persistedCfg.bubbleIconImageUrl || this.minimizedIconImageUrl;
      this.topSafeAreaColor =
        persistedCfg.topSafeAreaColor || this.topSafeAreaColor;
      this.bottomSafeAreaColor =
        persistedCfg.bottomSafeAreaColor || this.bottomSafeAreaColor;
      this.maximizedChatBackgroundColor =
        persistedCfg.chatBackground || this.maximizedChatBackgroundColor;
      this.customerBubbleBgColor =
        persistedCfg.customerBubbleBg || this.customerBubbleBgColor;
      this.customerBubbleTextColor =
        persistedCfg.customerBubbleText || this.customerBubbleTextColor;
      this.agentBubbleBgColor =
        persistedCfg.agentBubbleBg || this.agentBubbleBgColor;
      this.agentBubbleTextColor =
        persistedCfg.agentBubbleText || this.agentBubbleTextColor;
      this.systemMessageColor =
        persistedCfg.systemMessageColor || this.systemMessageColor;

      // Persisted midnight palette (stored in widget_theme_config)
      const midnightKeys = [
        'midnightVariant',
        'accent',
        'background',
        'surface',
        'border',
        'text',
        'mutedText',
        'subtleText',
        'input',
        'homeCard',
        'userBubbleBg',
        'userBubbleText',
        'agentBubbleBg',
        'agentBubbleText',
      ];
      const midnightFromDb = {};
      midnightKeys.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(persistedCfg, key)) {
          midnightFromDb[key] = persistedCfg[key];
        }
      });
      if (Object.keys(midnightFromDb).length) {
        this.midnightThemeConfig = {
          ...this.midnightThemeConfig,
          ...midnightFromDb,
        };
      }

      const savedInformation = this.getSavedInboxInformation();
      if (savedInformation) {
        // Preserve persisted inbox position when local cache doesn't have this field.
        this.widgetBubblePosition =
          savedInformation.position || this.widgetBubblePosition || 'right';
        this.widgetBubbleType = savedInformation.type || 'standard';
        this.widgetBubbleLauncherTitle =
          savedInformation.launcherTitle || 'Chat with us';
        this.themeMode = savedInformation.themeMode || this.themeMode;
        this.colorSettingsMode =
          savedInformation.colorSettingsMode || this.colorSettingsMode;
        this.sideSpacing = savedInformation.sideSpacing ?? this.sideSpacing;
        this.bottomSpacing =
          savedInformation.bottomSpacing ?? this.bottomSpacing;
        this.showMobileWidget =
          savedInformation.showMobileWidget ?? this.showMobileWidget;
        this.mobileMode = savedInformation.mobileMode || this.mobileMode;
        this.mobileSideSpacing =
          savedInformation.mobileSideSpacing ?? this.mobileSideSpacing;
        this.mobileBottomSpacing =
          savedInformation.mobileBottomSpacing ?? this.mobileBottomSpacing;
        this.showAgentPhoto =
          savedInformation.showAgentPhoto ?? this.showAgentPhoto;
        this.enableSoundForCustomers =
          savedInformation.enableSoundForCustomers ??
          this.enableSoundForCustomers;
        this.enableAgentRating =
          savedInformation.enableAgentRating ?? this.enableAgentRating;
        this.enableChatTranscript =
          savedInformation.enableChatTranscript ?? this.enableChatTranscript;
        this.showLogo = savedInformation.showLogo ?? this.showLogo;
        this.minimizedBubbleColor =
          savedInformation.minimizedBubbleColor || this.minimizedBubbleColor;
        this.minimizedIconColor =
          savedInformation.minimizedIconColor || this.minimizedIconColor;
        this.maximizedChatBackgroundColor =
          savedInformation.maximizedChatBackgroundColor ||
          this.maximizedChatBackgroundColor;
        this.maximizedChatBackgroundOpacity =
          savedInformation.maximizedChatBackgroundOpacity ||
          this.maximizedChatBackgroundOpacity;
        this.topSafeAreaColor =
          savedInformation.topSafeAreaColor || this.topSafeAreaColor;
        this.bottomSafeAreaColor =
          savedInformation.bottomSafeAreaColor || this.bottomSafeAreaColor;
        this.maximizedPrimaryColor =
          savedInformation.maximizedPrimaryColor || this.maximizedPrimaryColor;
        this.customerBubbleBgColor =
          savedInformation.customerBubbleBgColor || this.customerBubbleBgColor;
        this.customerBubbleTextColor =
          savedInformation.customerBubbleTextColor ||
          this.customerBubbleTextColor;
        this.agentBubbleBgColor =
          savedInformation.agentBubbleBgColor || this.agentBubbleBgColor;
        this.agentBubbleTextColor =
          savedInformation.agentBubbleTextColor || this.agentBubbleTextColor;
        this.systemMessageColor =
          savedInformation.systemMessageColor || this.systemMessageColor;
        this.widgetThemePreset =
          savedInformation.widgetThemePreset || this.widgetThemePreset;
        this.midnightThemeConfig = {
          ...this.midnightThemeConfig,
          ...(savedInformation.midnightThemeConfig || {}),
        };
        this.announcementText =
          savedInformation.announcementText ?? this.announcementText;
        this.announcementEnabled =
          savedInformation.announcementEnabled ?? this.announcementEnabled;
        this.announcementUrl =
          savedInformation.announcementUrl ?? this.announcementUrl;
        this.announcementBgColor =
          savedInformation.announcementBgColor || this.announcementBgColor;
        this.announcementTextColor =
          savedInformation.announcementTextColor || this.announcementTextColor;
      }

      // Persisted inbox theme config is the source of truth for classic colors.
      // Re-apply it after local cache so stale browser storage can't mask saved gradients.
      this.maximizedChatBackgroundColor =
        persistedCfg.chatBackground || this.maximizedChatBackgroundColor;
      this.customerBubbleBgColor =
        persistedCfg.customerBubbleBg || this.customerBubbleBgColor;
      this.customerBubbleTextColor =
        persistedCfg.customerBubbleText || this.customerBubbleTextColor;
      this.agentBubbleBgColor =
        persistedCfg.agentBubbleBg || this.agentBubbleBgColor;
      this.agentBubbleTextColor =
        persistedCfg.agentBubbleText || this.agentBubbleTextColor;
      this.systemMessageColor =
        persistedCfg.systemMessageColor || this.systemMessageColor;

      this.minimizedWindowStyle =
        this.widgetBubbleType === 'expanded_bubble' ? 'bar' : 'bubble';
      this.syncClassicThemeDefaults(this.themeMode);
      if (!this.topSafeAreaColor) {
        this.topSafeAreaColor =
          this.widgetThemePreset === 'midnight'
            ? this.midnightThemeConfig.background
            : this.maximizedChatBackgroundColor;
      }
      if (!this.bottomSafeAreaColor) {
        this.bottomSafeAreaColor =
          this.widgetThemePreset === 'midnight'
            ? this.midnightThemeConfig.background
            : this.maximizedChatBackgroundColor;
      }
    },
    toggleSection(section) {
      this.activeSection = this.activeSection === section ? '' : section;
    },
    selectMinimizedWindowStyle(style) {
      this.minimizedWindowStyle = style;
      this.widgetBubbleType = style === 'bar' ? 'expanded_bubble' : 'standard';
    },
    selectTheme(theme) {
      this.themeMode = theme;
    },
    selectThemeVariant(variant) {
      if (this.widgetThemePreset === 'midnight') {
        this.requestMidnightVariant(variant);
        return;
      }
      this.selectTheme(variant);
    },
    syncClassicThemeDefaults(mode) {
      if (this.widgetThemePreset !== 'classic') return;
      const nextTemplate =
        CLASSIC_THEME_TEMPLATES[mode] || CLASSIC_THEME_TEMPLATES.light;
      const allChatBackgrounds = Object.values(CLASSIC_THEME_TEMPLATES).map(
        template => template.chatBackground
      );
      const allCustomerBubbleTexts = Object.values(CLASSIC_THEME_TEMPLATES).map(
        template => template.customerBubbleText
      );
      const allAgentBubbleBgs = Object.values(CLASSIC_THEME_TEMPLATES).map(
        template => template.agentBubbleBg
      );
      const allAgentBubbleTexts = Object.values(CLASSIC_THEME_TEMPLATES).map(
        template => template.agentBubbleText
      );
      const allSystemMessageColors = Object.values(CLASSIC_THEME_TEMPLATES).map(
        template => template.systemMessageColor
      );

      if (
        !this.maximizedChatBackgroundColor ||
        allChatBackgrounds.includes(this.maximizedChatBackgroundColor)
      ) {
        this.maximizedChatBackgroundColor = nextTemplate.chatBackground;
      }
      if (
        !this.customerBubbleTextColor ||
        allCustomerBubbleTexts.includes(this.customerBubbleTextColor)
      ) {
        this.customerBubbleTextColor = nextTemplate.customerBubbleText;
      }
      if (
        !this.agentBubbleBgColor ||
        allAgentBubbleBgs.includes(this.agentBubbleBgColor)
      ) {
        this.agentBubbleBgColor = nextTemplate.agentBubbleBg;
      }
      if (
        !this.agentBubbleTextColor ||
        allAgentBubbleTexts.includes(this.agentBubbleTextColor)
      ) {
        this.agentBubbleTextColor = nextTemplate.agentBubbleText;
      }
      if (
        !this.systemMessageColor ||
        allSystemMessageColors.includes(this.systemMessageColor)
      ) {
        this.systemMessageColor = nextTemplate.systemMessageColor;
      }
    },
    // Backward-compat (older call sites)
    selectThemeColor(color) {
      this.applyThemeSwatch(color);
    },
    applyMidnightTemplate(variant) {
      const tpl =
        MIDNIGHT_THEME_TEMPLATES[variant] || MIDNIGHT_THEME_TEMPLATES.dark;
      // Replace (reset) all midnight custom colors to the chosen template
      this.midnightThemeConfig = { ...tpl };
    },
    async requestMidnightVariant(variant) {
      if (this.widgetThemePreset !== 'midnight') return;
      const current = this.midnightVariant;
      if (variant === current) return;

      this.pendingMidnightVariant = variant;
      const ok = await this.$refs?.midnightVariantConfirm?.showConfirmation?.();
      if (ok) {
        this.applyMidnightTemplate(variant);
        // Keep widget primary colors aligned to the template accent
        if (this.midnightThemeConfig?.accent) {
          this.applyThemeSwatch(this.midnightThemeConfig.accent);
        }
      }
      this.pendingMidnightVariant = null;
    },
    applyThemeSwatch(color) {
      // Theme swatch sets a coherent palette (primary + launcher + customer bubble)
      this.maximizedPrimaryColor = color;
      this.color = color;
      this.minimizedBubbleColor = color;
      this.customerBubbleBgColor = color;
    },
    applyClassicGradientTheme(preset) {
      if (!preset?.value) return;

      const baseColor =
        (preset.preview || this.color || CLASSIC_DEFAULT_PRIMARY).toString();

      // Keep classic primary surfaces on a solid accent,
      // while letting the customer bubble use the selected gradient.
      this.maximizedPrimaryColor = baseColor;
      this.color = baseColor;
      this.minimizedBubbleColor = baseColor;
      this.customerBubbleBgColor = preset.value;
      this.customerBubbleTextColor = getContrastingTextColor(baseColor);
    },
    normalizeHex(value) {
      const raw = (value || '').toString().trim();
      const match = raw.match(/^#?([0-9a-f]{6})$/i);
      return match ? `#${match[1].toLowerCase()}` : '#000000';
    },
    openColorPicker(target, currentValue) {
      this.activeColorPickerTarget = target;
      this.activeColorPickerValue = this.normalizeHex(currentValue);
      this.isColorPopoverOpen = true;
      this.$nextTick(() => {
        this.positionColorPopover();
      });
    },
    positionColorPopover() {
      const triggerEl =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      const rect = triggerEl?.getBoundingClientRect?.();
      const popoverWidth = 230;
      const safeLeft = rect
        ? Math.min(
            Math.max(10, rect.left),
            Math.max(10, window.innerWidth - popoverWidth - 10)
          )
        : 20;
      this.colorPopoverLeft = Math.round(safeLeft);
      this.colorPopoverTop = Math.round((rect?.bottom || 20) + 8);
    },
    closeColorPicker() {
      this.isColorPopoverOpen = false;
      this.activeColorPickerTarget = null;
    },
    handleGlobalClick(event) {
      if (!this.isColorPopoverOpen) return;
      const popover = this.$refs.colorPopover;
      const target = event?.target;
      if (popover?.contains?.(target)) return;
      if (target?.closest?.('.color-swatch-btn')) return;
      this.closeColorPicker();
    },
    onColorPopoverUpdate(payload) {
      const hex = this.normalizeHex(payload?.hex || payload);
      this.setColorByTarget(this.activeColorPickerTarget, hex);
    },
    setColorByTarget(target, hex) {
      if (!target) return;
      if (target.includes('.')) {
        const [root, key] = target.split('.');
        if (root === 'midnightThemeConfig' && key) {
          this.midnightThemeConfig = {
            ...this.midnightThemeConfig,
            [key]: hex,
          };
          if (key === 'accent') {
            this.applyThemeSwatch(hex);
          }
        }
      } else {
        this[target] = hex;
        if (target === 'maximizedPrimaryColor') {
          this.color = hex;
        }
      }
      this.activeColorPickerValue = hex;
      // Push settings only – no rebuild (rebuild closes the preview widget)
      this.pushSettingsToPreview();
    },
    selectWidgetPosition(position) {
      this.widgetBubblePosition = position;
    },
    handleLogoUpload(event) {
      const file = event?.target?.files?.[0];
      if (!file) return;

      this.avatarFile = file;
      this.avatarUrl = URL.createObjectURL(file);
      this.showLogo = true;
    },
    async copyScript() {
      if (!this.widgetScript) return;
      try {
        await navigator.clipboard.writeText(this.widgetScript);
        useAlert('Widget script copied');
      } catch (error) {
        useAlert('Unable to copy script');
      }
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) {
        useAlert('Website URL is not configured for this inbox.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async updateWidget() {
      const bubbleSettings = {
        position: this.widgetBubblePosition,
        launcherTitle: this.widgetBubbleLauncherTitle,
        type: this.widgetBubbleType,
        themeMode: this.themeMode,
        colorSettingsMode: this.colorSettingsMode,
        widgetThemePreset: this.widgetThemePreset,
        midnightThemeConfig: this.midnightThemeConfig,
        announcementEnabled: this.announcementEnabled,
        announcementText: this.announcementText,
        announcementUrl: this.announcementUrl,
        announcementBgColor: this.announcementBgColor,
        announcementTextColor: this.announcementTextColor,
        minimizedBubbleColor: this.minimizedBubbleColor,
        minimizedIconColor: this.minimizedIconColor,
        minimizedIconImageUrl: this.minimizedIconImageUrl,
        maximizedChatBackgroundColor: this.maximizedChatBackgroundColor,
        maximizedChatBackgroundOpacity: this.maximizedChatBackgroundOpacity,
        topSafeAreaColor: this.topSafeAreaColor,
        bottomSafeAreaColor: this.bottomSafeAreaColor,
        maximizedPrimaryColor: this.maximizedPrimaryColor,
        customerBubbleBgColor: this.customerBubbleBgColor,
        customerBubbleTextColor: this.customerBubbleTextColor,
        agentBubbleBgColor: this.agentBubbleBgColor,
        agentBubbleTextColor: this.agentBubbleTextColor,
        systemMessageColor: this.systemMessageColor,
      };

      LocalStorage.set(this.storageKey, bubbleSettings);

      try {
        const existingThemeCfg = this.inbox.widget_theme_config || {};
        const nextThemeCfg = {
          ...existingThemeCfg,
          ...(this.liveChatwootSettings.themeConfig || {}),
        };
        const payload = {
          id: this.inbox.id,
          name: this.websiteName || this.inbox.name,
          channel: {
            widget_color: this.color,
            welcome_title: this.welcomeHeading,
            welcome_tagline: this.welcomeTagline,
            reply_time: this.replyTime,
            widget_theme_preset: this.widgetThemePreset,
            widget_theme_config: nextThemeCfg,
            announcement_text: this.announcementText,
            announcement_url: this.announcementUrl,
            announcement_bg_color: this.announcementBgColor,
            announcement_text_color: this.announcementTextColor,
            skip_start_conversation: this.skipStartConversation,
            widget_language: this.widgetLanguage,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }

        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.SUCCESS_MESSAGE'
          )
        );
      } catch (error) {
        useAlert(
          error.message ||
            this.$t(
              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.API.ERROR_MESSAGE'
            )
        );
      }
    },
    getSavedInboxInformation() {
      return LocalStorage.get(this.storageKey);
    },
  },
};
</script>

<template>
  <div
    class="widget-builder-page wb-scroll-layout"
    :class="{ 'is-dark': isDark }"
  >
    <div
      v-if="isColorPopoverOpen"
      ref="colorPopover"
      class="widget-color-popover"
      :style="{ top: `${colorPopoverTop}px`, left: `${colorPopoverLeft}px` }"
    >
      <Chrome
        disable-alpha
        :model-value="activeColorPickerValue"
        class="widget-color-popover__chrome"
        @update:model-value="onColorPopoverUpdate"
      />
    </div>
    <div class="widget-builder-layout">
      <section class="builder-config-panel">
        <header class="builder-header">
          <div class="builder-title">Customize your chat widget</div>
        </header>

        <div class="builder-sections">
          <p class="builder-subtitle">
            Decide how the chat widget on your website will look, behave and
            what information it will offer.
          </p>
          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'website'"
              @click="toggleSection('website')"
            >
              <span class="section-left">
                <span class="i-lucide-globe section-icon" />
                <span class="section-name">Customize widget on website</span>
                <span class="section-badge">new!</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div v-if="activeSection === 'website'" class="section-content">
              <p class="section-help">
                Install this script on your website and use beta SDK preview for
                a near 1:1 render.
              </p>
              <div class="script-box">
                <woot-code :script="widgetScript" />
              </div>
              <div class="section-actions">
                <button class="ghost-btn" @click="copyScript">Copy code</button>
                <button class="ghost-btn" @click="openWebsitePreview">
                  Open chat page
                </button>
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'appearance'"
              @click="toggleSection('appearance')"
            >
              <span class="section-left">
                <span class="i-lucide-palette section-icon" />
                <span class="section-name">Appearance</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div v-if="activeSection === 'appearance'" class="section-content">
              <div class="option-block">
                <p class="option-title">Minimized window</p>
                <div class="option-grid two">
                  <button
                    class="choice-card"
                    :class="{ active: minimizedWindowStyle === 'bar' }"
                    type="button"
                    @click="selectMinimizedWindowStyle('bar')"
                  >
                    <span
                      class="choice-box"
                      :class="{ active: minimizedWindowStyle === 'bar' }"
                    >
                      <span class="choice-svg">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="32"
                          height="32"
                        >
                          <path
                            fill="currentColor"
                            d="M20.022 11.02a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M20.022 13.026q.243 0 .478-.033V15h.5a1 1 0 1 1 0 2H3a1 1 0 1 1 0-2h.5v-3.724a2 2 0 0 1 2-2h11.025q-.008.12-.008.245a3.505 3.505 0 0 0 3.505 3.505"
                          />
                        </svg>
                      </span>
                      <span
                        class="choice-check"
                        :class="{ show: minimizedWindowStyle === 'bar' }"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m5 12 5 5L20 7"
                          />
                        </svg>
                      </span>
                    </span>
                    <span class="choice-text">Bar</span>
                  </button>
                  <button
                    class="choice-card"
                    :class="{ active: minimizedWindowStyle === 'bubble' }"
                    type="button"
                    @click="selectMinimizedWindowStyle('bubble')"
                  >
                    <span
                      class="choice-box"
                      :class="{ active: minimizedWindowStyle === 'bubble' }"
                    >
                      <span class="choice-svg">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="32"
                          height="32"
                        >
                          <path
                            fill="currentColor"
                            d="M19.373 8.89Q18.953 9 18.5 9a3.5 3.5 0 0 1-3.39-4.373 8 8 0 1 0 4.263 4.263M20 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"
                          />
                        </svg>
                      </span>
                      <span
                        class="choice-check"
                        :class="{ show: minimizedWindowStyle === 'bubble' }"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m5 12 5 5L20 7"
                          />
                        </svg>
                      </span>
                    </span>
                    <span class="choice-text">Bubble</span>
                  </button>
                </div>
              </div>

              <div class="option-block">
                <p class="option-title">Widget theme</p>
                <p class="option-hint">
                  Choose one of the ready-made widget themes. You can still
                  customize colors below.
                </p>
                <div class="option-grid two">
                  <button
                    class="choice-card"
                    :class="{ active: widgetThemePreset === 'classic' }"
                    type="button"
                    @click="widgetThemePreset = 'classic'"
                  >
                    <span
                      class="choice-box"
                      :class="{ active: widgetThemePreset === 'classic' }"
                    >
                      <span class="choice-svg subtle">
                        <span class="theme-mini theme-mini--classic" />
                      </span>
                      <span
                        class="choice-check"
                        :class="{ show: widgetThemePreset === 'classic' }"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m5 12 5 5L20 7"
                          />
                        </svg>
                      </span>
                    </span>
                    <span class="choice-text">Classic</span>
                  </button>
                  <button
                    class="choice-card"
                    :class="{ active: widgetThemePreset === 'midnight' }"
                    type="button"
                    @click="widgetThemePreset = 'midnight'"
                  >
                    <span
                      class="choice-box"
                      :class="{ active: widgetThemePreset === 'midnight' }"
                    >
                      <span class="choice-svg subtle">
                        <span class="theme-mini theme-mini--midnight" />
                      </span>
                      <span
                        class="choice-check"
                        :class="{ show: widgetThemePreset === 'midnight' }"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="16"
                          height="16"
                        >
                          <path
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m5 12 5 5L20 7"
                          />
                        </svg>
                      </span>
                    </span>
                    <span class="choice-text">Midnight</span>
                  </button>
                </div>
                <div class="option-block">
                  <p class="option-title">
                    {{
                      widgetThemePreset === 'midnight'
                        ? 'Midnight template'
                        : 'Classic mode'
                    }}
                  </p>
                  <p class="option-hint">
                    {{
                      widgetThemePreset === 'midnight'
                        ? 'Switching template resets the Midnight custom colors below.'
                        : 'Choose the Classic light or dark mode before customizing colors below.'
                    }}
                  </p>
                  <div class="option-grid two">
                    <button
                      class="choice-card"
                      :class="{
                        active:
                          (widgetThemePreset === 'midnight'
                            ? midnightVariant
                            : themeMode) === 'light',
                      }"
                      type="button"
                      @click="selectThemeVariant('light')"
                    >
                      <span
                        class="choice-box"
                        :class="{
                          active:
                            (widgetThemePreset === 'midnight'
                              ? midnightVariant
                              : themeMode) === 'light',
                        }"
                      >
                        <span class="choice-svg subtle">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="32"
                            height="32"
                          >
                            <path
                              fill="currentColor"
                              d="M12 19a1 1 0 0 1 .993.883L13 20v1a1 1 0 0 1-1.993.117L11 21v-1a1 1 0 0 1 1-1M18.313 16.91l.094.083.7.7a1 1 0 0 1-1.32 1.497l-.094-.083-.7-.7a1 1 0 0 1 1.218-1.567zM7.007 16.993a1 1 0 0 1 .083 1.32l-.083.094-.7.7a1 1 0 0 1-1.497-1.32l.083-.094.7-.7a1 1 0 0 1 1.414 0M4 11a1 1 0 0 1 .117 1.993L4 13H3a1 1 0 0 1-.117-1.993L3 11zM21 11a1 1 0 0 1 .117 1.993L21 13h-1a1 1 0 0 1-.117-1.993L20 11zM6.213 4.81l.094.083.7.7a1 1 0 0 1-1.32 1.497l-.094-.083-.7-.7A1 1 0 0 1 6.11 4.74zM19.107 4.893a1 1 0 0 1 .083 1.32l-.083.094-.7.7a1 1 0 0 1-1.497-1.32l.083-.094.7-.7a1 1 0 0 1 1.414 0M12 2a1 1 0 0 1 .993.883L13 3v1a1 1 0 0 1-1.993.117L11 4V3a1 1 0 0 1 1-1M12 7a5 5 0 1 1-4.995 5.217L7 12l.005-.217A5 5 0 0 1 12 7"
                            />
                          </svg>
                        </span>
                        <span
                          class="choice-check"
                          :class="{
                            show:
                              (widgetThemePreset === 'midnight'
                                ? midnightVariant
                                : themeMode) === 'light',
                          }"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m5 12 5 5L20 7"
                            />
                          </svg>
                        </span>
                      </span>
                      <span class="choice-text">Light</span>
                    </button>
                    <button
                      class="choice-card"
                      :class="{
                        active:
                          (widgetThemePreset === 'midnight'
                            ? midnightVariant
                            : themeMode) === 'dark',
                      }"
                      type="button"
                      @click="selectThemeVariant('dark')"
                    >
                      <span
                        class="choice-box"
                        :class="{
                          active:
                            (widgetThemePreset === 'midnight'
                              ? midnightVariant
                              : themeMode) === 'dark',
                        }"
                      >
                        <span class="choice-svg subtle">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="32"
                            height="32"
                          >
                            <path
                              fill="currentColor"
                              d="M12 1.992a10 10 0 1 0 9.236 13.838c.341-.82-.476-1.644-1.298-1.31a6.5 6.5 0 0 1-6.864-10.787l.077-.08c.551-.63.113-1.653-.758-1.653h-.266l-.068-.006z"
                            />
                          </svg>
                        </span>
                        <span
                          class="choice-check"
                          :class="{
                            show:
                              (widgetThemePreset === 'midnight'
                                ? midnightVariant
                                : themeMode) === 'dark',
                          }"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                          >
                            <path
                              stroke="currentColor"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="m5 12 5 5L20 7"
                            />
                          </svg>
                        </span>
                      </span>
                      <span class="choice-text">Dark</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="option-block">
                <p class="option-title">
                  {{
                    widgetThemePreset === 'midnight'
                      ? 'Midnight customize'
                      : 'Theme and colors'
                  }}
                </p>
                <p v-if="widgetThemePreset !== 'midnight'" class="option-hint">
                  Adjust the selected Classic light/dark palette and the main
                  accent color.
                </p>
                <p v-else class="option-hint">
                  Choose the Midnight template (Dark/Light) above. Customize the
                  palette here. Switching template will reset these colors.
                </p>

                <div
                  v-if="widgetThemePreset === 'midnight'"
                  class="advanced-colors"
                  style="padding-top: 0"
                >
                  <div class="color-grid">
                    <div class="color-field">
                      <label>Accent</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.accent"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.accent',
                              midnightThemeConfig.accent
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: midnightThemeConfig.accent }"
                          />
                          <span class="i-lucide-check size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Background</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.background"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.background',
                              midnightThemeConfig.background
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background: midnightThemeConfig.background,
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Surface</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.surface"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.surface',
                              midnightThemeConfig.surface
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: midnightThemeConfig.surface }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Border</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.border"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.border',
                              midnightThemeConfig.border
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: midnightThemeConfig.border }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Text</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.text"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.text',
                              midnightThemeConfig.text
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: midnightThemeConfig.text }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Muted text</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.mutedText"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.mutedText',
                              midnightThemeConfig.mutedText
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background: midnightThemeConfig.mutedText,
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Input</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.input"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.input',
                              midnightThemeConfig.input
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: midnightThemeConfig.input }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Home card</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.homeCard"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.homeCard',
                              midnightThemeConfig.homeCard
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background: midnightThemeConfig.homeCard,
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="advanced-title" style="margin-top: 14px">
                    iPhone safe areas
                  </div>
                  <div class="color-grid">
                    <div class="color-field">
                      <label>Top safe area</label>
                      <div class="color-input">
                        <input
                          v-model="topSafeAreaColor"
                          class="manual-input"
                          spellcheck="false"
                          :placeholder="
                            midnightThemeConfig.background || '#111111'
                          "
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'topSafeAreaColor',
                              topSafeAreaColor ||
                                midnightThemeConfig.background ||
                                '#111111'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                topSafeAreaColor ||
                                midnightThemeConfig.background ||
                                '#111111',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Bottom safe area</label>
                      <div class="color-input">
                        <input
                          v-model="bottomSafeAreaColor"
                          class="manual-input"
                          spellcheck="false"
                          :placeholder="
                            midnightThemeConfig.background || '#111111'
                          "
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'bottomSafeAreaColor',
                              bottomSafeAreaColor ||
                                midnightThemeConfig.background ||
                                '#111111'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                bottomSafeAreaColor ||
                                midnightThemeConfig.background ||
                                '#111111',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="advanced-title" style="margin-top: 14px">
                    User bubble gradient presets
                  </div>
                  <div class="midnight-bubble-presets">
                    <button
                      v-for="preset in midnightBubblePresets"
                      :key="preset.value"
                      class="mn-preset-btn"
                      :class="{
                        active:
                          midnightThemeConfig.userBubbleBg === preset.value,
                      }"
                      type="button"
                      :title="preset.label"
                      :style="{ background: preset.value }"
                      @click="
                        midnightThemeConfig = {
                          ...midnightThemeConfig,
                          userBubbleBg: preset.value,
                        }
                      "
                    />
                  </div>
                  <div class="advanced-title" style="margin-top: 10px">
                    Messages
                  </div>
                  <div class="color-grid">
                    <div class="color-field">
                      <label>User bubble bg</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.userBubbleBg"
                          class="manual-input"
                          spellcheck="false"
                          placeholder="#ff8731"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.userBubbleBg',
                              midnightThemeConfig.userBubbleBg || '#ff8731'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                midnightThemeConfig.userBubbleBg || '#ff8731',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>User bubble text</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.userBubbleText"
                          class="manual-input"
                          spellcheck="false"
                          placeholder="#ffffff"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.userBubbleText',
                              midnightThemeConfig.userBubbleText || '#ffffff'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                midnightThemeConfig.userBubbleText || '#ffffff',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Agent bubble bg</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.agentBubbleBg"
                          class="manual-input"
                          spellcheck="false"
                          placeholder="#252525"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.agentBubbleBg',
                              midnightThemeConfig.agentBubbleBg || '#252525'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                midnightThemeConfig.agentBubbleBg || '#252525',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Agent bubble text</label>
                      <div class="color-input">
                        <input
                          v-model="midnightThemeConfig.agentBubbleText"
                          class="manual-input"
                          spellcheck="false"
                          placeholder="#ffffff"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'midnightThemeConfig.agentBubbleText',
                              midnightThemeConfig.agentBubbleText || '#ffffff'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                midnightThemeConfig.agentBubbleText ||
                                '#ffffff',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="widgetThemePreset !== 'midnight'"
                  class="color-mode-list"
                >
                  <label
                    class="color-mode"
                    :class="{ selected: colorSettingsMode === 'theme' }"
                  >
                    <span class="circle">
                      <span class="inner-circle" />
                      <input
                        type="radio"
                        name="colorSettingsMode"
                        value="theme"
                        :checked="colorSettingsMode === 'theme'"
                        @change="colorSettingsMode = 'theme'"
                      />
                    </span>
                    <span class="content">
                      <span class="color-mode-title">Theme color</span>
                    </span>
                  </label>
                  <label
                    class="color-mode"
                    :class="{ selected: colorSettingsMode === 'more' }"
                  >
                    <span class="circle">
                      <span class="inner-circle" />
                      <input
                        type="radio"
                        name="colorSettingsMode"
                        value="more"
                        :checked="colorSettingsMode === 'more'"
                        @change="colorSettingsMode = 'more'"
                      />
                    </span>
                    <span class="content">
                      <span class="color-mode-title">More color settings</span>
                    </span>
                  </label>
                </div>

                <div
                  v-if="
                    widgetThemePreset !== 'midnight' &&
                    colorSettingsMode === 'theme'
                  "
                >
                  <!-- Solid color swatches -->
                  <div class="swatch-row">
                    <button
                      v-for="swatch in themeSwatches"
                      :key="swatch"
                      class="swatch-dot"
                      :class="{ active: color === swatch }"
                      :style="{ backgroundColor: swatch }"
                      type="button"
                      @click="applyThemeSwatch(swatch)"
                    >
                      <span class="swatch-hit" />
                    </button>
                  </div>
                  <!-- Gradient presets for classic theme color -->
                  <p class="advanced-title" style="margin-top: 12px">
                    Theme color gradient presets
                  </p>
                  <p
                    class="option-hint"
                    style="margin-bottom: 6px; font-size: 11px; color: #64748b"
                  >
                    Click to apply a matched classic theme with a gradient
                    Customer bubble background
                  </p>
                  <div class="midnight-bubble-presets" style="margin-top: 0">
                    <button
                      v-for="preset in midnightBubblePresets"
                      :key="preset.value"
                      class="mn-preset-btn"
                      :class="{
                        active: customerBubbleBgColor === preset.value,
                      }"
                      type="button"
                      :title="preset.label"
                      :style="{ background: preset.value }"
                      @click="applyClassicGradientTheme(preset)"
                    />
                  </div>
                </div>

                <div
                  v-else-if="widgetThemePreset !== 'midnight'"
                  class="advanced-colors"
                >
                  <p class="advanced-hint">
                    Make the chat widget more unique by choosing a custom color
                    scheme.
                  </p>

                  <div class="advanced-title">Minimized widget</div>
                  <div class="color-grid">
                    <div class="color-field">
                      <label>Bubble</label>
                      <div class="color-input">
                        <input
                          v-model="minimizedBubbleColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          :title="minimizedBubbleColor"
                          @click="
                            openColorPicker(
                              'minimizedBubbleColor',
                              minimizedBubbleColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: minimizedBubbleColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Icon color</label>
                      <div class="color-input">
                        <input
                          v-model="minimizedIconColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          :title="minimizedIconColor"
                          @click="
                            openColorPicker(
                              'minimizedIconColor',
                              minimizedIconColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: minimizedIconColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="advanced-title">Maximized widget</div>
                  <div class="color-grid">
                    <div class="color-field">
                      <label>Chat background</label>
                      <div class="color-input">
                        <input
                          v-model="maximizedChatBackgroundColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          :title="maximizedChatBackgroundColor"
                          @click="
                            openColorPicker(
                              'maximizedChatBackgroundColor',
                              maximizedChatBackgroundColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background: maximizedChatBackgroundColor,
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Opacity</label>
                      <input
                        v-model="maximizedChatBackgroundOpacity"
                        class="opacity-input"
                        maxlength="4"
                        spellcheck="false"
                      />
                    </div>
                    <div class="color-field">
                      <label>Primary color</label>
                      <div class="color-input">
                        <input
                          v-model="maximizedPrimaryColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          :title="maximizedPrimaryColor"
                          @click="
                            openColorPicker(
                              'maximizedPrimaryColor',
                              maximizedPrimaryColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: maximizedPrimaryColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="advanced-title" style="margin-top: 14px">
                    iPhone safe areas
                  </div>
                  <div class="color-grid">
                    <div class="color-field">
                      <label>Top safe area</label>
                      <div class="color-input">
                        <input
                          v-model="topSafeAreaColor"
                          class="manual-input"
                          spellcheck="false"
                          :placeholder="
                            maximizedChatBackgroundColor || '#f6f6f7'
                          "
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          :title="
                            topSafeAreaColor || maximizedChatBackgroundColor
                          "
                          @click="
                            openColorPicker(
                              'topSafeAreaColor',
                              topSafeAreaColor ||
                                maximizedChatBackgroundColor ||
                                '#f6f6f7'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                topSafeAreaColor ||
                                maximizedChatBackgroundColor ||
                                '#f6f6f7',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Bottom safe area</label>
                      <div class="color-input">
                        <input
                          v-model="bottomSafeAreaColor"
                          class="manual-input"
                          spellcheck="false"
                          :placeholder="
                            maximizedChatBackgroundColor || '#f6f6f7'
                          "
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          :title="
                            bottomSafeAreaColor || maximizedChatBackgroundColor
                          "
                          @click="
                            openColorPicker(
                              'bottomSafeAreaColor',
                              bottomSafeAreaColor ||
                                maximizedChatBackgroundColor ||
                                '#f6f6f7'
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{
                              background:
                                bottomSafeAreaColor ||
                                maximizedChatBackgroundColor ||
                                '#f6f6f7',
                            }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div class="advanced-title">Messages</div>
                  <div class="color-grid">
                    <div class="color-field">
                      <label>Customer&apos;s bubble</label>
                      <div class="color-input">
                        <input
                          v-model="customerBubbleBgColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'customerBubbleBgColor',
                              customerBubbleBgColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: customerBubbleBgColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Customer&apos;s text</label>
                      <div class="color-input">
                        <input
                          v-model="customerBubbleTextColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'customerBubbleTextColor',
                              customerBubbleTextColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: customerBubbleTextColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Agent&apos;s bubble</label>
                      <div class="color-input">
                        <input
                          v-model="agentBubbleBgColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'agentBubbleBgColor',
                              agentBubbleBgColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: agentBubbleBgColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>Agent&apos;s text</label>
                      <div class="color-input">
                        <input
                          v-model="agentBubbleTextColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'agentBubbleTextColor',
                              agentBubbleTextColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: agentBubbleTextColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                    <div class="color-field">
                      <label>System messages</label>
                      <div class="color-input">
                        <input
                          v-model="systemMessageColor"
                          class="manual-input"
                          spellcheck="false"
                        />
                        <button
                          class="color-swatch-btn"
                          type="button"
                          @click="
                            openColorPicker(
                              'systemMessageColor',
                              systemMessageColor
                            )
                          "
                        >
                          <span
                            class="swatch-square"
                            :style="{ background: systemMessageColor }"
                          />
                          <span class="i-lucide-chevron-down size-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'announcement'"
              @click="toggleSection('announcement')"
            >
              <span class="section-left">
                <span class="i-lucide-megaphone section-icon" />
                <span class="section-name">Announcement banner</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div
              v-if="activeSection === 'announcement'"
              class="section-content"
            >
              <label class="switch-row">
                <input v-model="announcementEnabled" type="checkbox" />
                <span>Show announcement banner</span>
              </label>
              <p class="option-hint">
                Shows a small banner at the top of the widget. Turn the switch
                off to hide it, or leave the text empty to disable it.
              </p>
              <div class="announcement-panel">
                <div class="announcement-grid">
                  <div class="announcement-field full">
                    <label>Text</label>
                    <input
                      v-model="announcementText"
                      class="manual-input manual-input--wide"
                      spellcheck="false"
                      placeholder="Remember our backup link https://www.aw8npl2.com/"
                    />
                  </div>
                  <div class="announcement-field full">
                    <label>Link (optional)</label>
                    <input
                      v-model="announcementUrl"
                      class="manual-input manual-input--wide"
                      spellcheck="false"
                      placeholder="aw8npl3.com"
                    />
                  </div>
                  <div class="announcement-field">
                    <label>Background</label>
                    <div class="color-input">
                      <input
                        v-model="announcementBgColor"
                        class="manual-input"
                        spellcheck="false"
                      />
                      <button
                        class="color-swatch-btn"
                        type="button"
                        @click="
                          openColorPicker(
                            'announcementBgColor',
                            announcementBgColor
                          )
                        "
                      >
                        <span
                          class="swatch-square"
                          :style="{ background: announcementBgColor }"
                        />
                        <span class="i-lucide-chevron-down size-4" />
                      </button>
                    </div>
                  </div>
                  <div class="announcement-field">
                    <label>Text color</label>
                    <div class="color-input">
                      <input
                        v-model="announcementTextColor"
                        class="manual-input"
                        spellcheck="false"
                      />
                      <button
                        class="color-swatch-btn"
                        type="button"
                        @click="
                          openColorPicker(
                            'announcementTextColor',
                            announcementTextColor
                          )
                        "
                      >
                        <span
                          class="swatch-square"
                          :style="{ background: announcementTextColor }"
                        />
                        <span class="i-lucide-chevron-down size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'position'"
              @click="toggleSection('position')"
            >
              <span class="section-left">
                <span class="i-lucide-move section-icon" />
                <span class="section-name">Position</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div v-if="activeSection === 'position'" class="section-content">
              <div class="inline-controls">
                <label class="field">
                  <span>Align to</span>
                  <select
                    :value="widgetBubblePosition"
                    @change="selectWidgetPosition($event.target.value)"
                  >
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                  </select>
                </label>
                <label class="field">
                  <span>Side spacing</span>
                  <input v-model.number="sideSpacing" type="number" min="0" />
                </label>
                <label class="field">
                  <span>Bottom spacing</span>
                  <input v-model.number="bottomSpacing" type="number" min="0" />
                </label>
              </div>
              <div class="option-block">
                <p class="option-title">Visibility</p>
                <label class="radio-line">
                  <input
                    v-model="visibilityMode"
                    value="always_visible"
                    type="radio"
                  />
                  <span>The chat widget is always visible</span>
                </label>
                <label class="radio-line">
                  <input
                    v-model="visibilityMode"
                    value="hide_until_greeted"
                    type="radio"
                  />
                  <span>Hide widget until it gets activated</span>
                </label>
                <label class="radio-line disabled">
                  <input
                    v-model="visibilityMode"
                    value="always_minimized"
                    type="radio"
                    disabled
                  />
                  <span>Always hide minimized widget icon</span>
                </label>
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'mobile'"
              @click="toggleSection('mobile')"
            >
              <span class="section-left">
                <span class="i-lucide-smartphone section-icon" />
                <span class="section-name">Mobile chat widget</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div v-if="activeSection === 'mobile'" class="section-content">
              <label class="switch-row">
                <input v-model="showMobileWidget" type="checkbox" />
                <span>Show chat widget on mobile</span>
              </label>
              <div class="option-block">
                <label class="radio-line">
                  <input
                    v-model="mobileMode"
                    value="same_as_desktop"
                    type="radio"
                  />
                  <span>Same as on desktop</span>
                </label>
                <label class="radio-line">
                  <input
                    v-model="mobileMode"
                    value="custom_mobile"
                    type="radio"
                  />
                  <span>Custom for mobile devices</span>
                </label>
                <div
                  v-if="mobileMode === 'custom_mobile'"
                  class="inline-controls"
                  style="margin-top: 10px"
                >
                  <label class="field">
                    <span>Mobile side spacing</span>
                    <input
                      v-model.number="mobileSideSpacing"
                      type="number"
                      min="0"
                    />
                  </label>
                  <label class="field">
                    <span>Mobile bottom spacing</span>
                    <input
                      v-model.number="mobileBottomSpacing"
                      type="number"
                      min="0"
                    />
                  </label>
                </div>
              </div>
            </div>
          </article>

          <article class="builder-section">
            <button
              class="section-trigger"
              type="button"
              :aria-expanded="activeSection === 'tweaks'"
              @click="toggleSection('tweaks')"
            >
              <span class="section-left">
                <span class="i-lucide-sliders-horizontal section-icon" />
                <span class="section-name">Additional tweaks</span>
              </span>
              <span class="i-lucide-chevron-right section-chevron" />
            </button>
            <div v-if="activeSection === 'tweaks'" class="section-content">
              <div class="tweak-row">
                <label class="switch-row m-0">
                  <input v-model="showLogo" type="checkbox" />
                  <span>Show logo</span>
                </label>
                <label class="upload-link">
                  Upload your logo
                  <input
                    class="hidden-upload"
                    type="file"
                    accept="image/*"
                    @change="handleLogoUpload"
                  />
                </label>
              </div>
              <label class="switch-row">
                <input v-model="showAgentPhoto" type="checkbox" />
                <span>Show agent&apos;s photo</span>
              </label>
              <label class="switch-row">
                <input v-model="enableSoundForCustomers" type="checkbox" />
                <span>Enable sound notifications for customers</span>
              </label>
              <label class="switch-row">
                <input v-model="enableAgentRating" type="checkbox" />
                <span>Let customers rate agents</span>
              </label>
              <label class="switch-row">
                <input v-model="enableChatTranscript" type="checkbox" />
                <span>Let customers get chat transcripts</span>
              </label>
              <label class="switch-row disabled">
                <input v-model="whiteLabelLocked" type="checkbox" disabled />
                <span>White label widget</span>
                <span class="i-lucide-lock size-3.5" />
              </label>
            </div>
          </article>
        </div>

        <div class="save-row">
          <NextButton
            class="lc-save-btn"
            type="button"
            :label="
              $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.UPDATE.BUTTON_TEXT')
            "
            :is-loading="uiFlags.isUpdating"
            :disabled="uiFlags.isUpdating"
            @click="updateWidget"
          />
        </div>
      </section>

      <section class="builder-preview-panel">
        <div class="preview-header-bar">
          <div class="preview-header-title">Preview</div>
        </div>
        <div class="preview-content-area">
          <div class="preview-scroll-inner">
            <div class="preview-widget-holder">
              <div class="preview-widget-stage">
                <div class="preview-widget-inner">
                  <iframe
                    v-if="previewDocHtml"
                    ref="sdkFrame"
                    class="sdk-preview-frame"
                    :srcdoc="previewDocHtml"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                  />
                  <div v-else class="preview-empty">
                    Widget preview is unavailable for this inbox.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <ConfirmationModal
      ref="midnightVariantConfirm"
      :title="midnightVariantConfirmTitle"
      :description="midnightVariantConfirmDescription"
      :confirm-label="midnightVariantConfirmLabel"
      cancel-label="Cancel"
    />
  </div>
</template>

<style scoped>
/* ── Design tokens – light defaults, dark overrides ── */
.widget-builder-page {
  --wb-bg: #ffffff;
  --wb-panel: #ffffff;
  --wb-border: rgba(15, 23, 42, 0.1);
  --wb-section-bg: #ffffff;
  --wb-section-border: rgba(15, 23, 42, 0.08);
  --wb-text-primary: #000000;
  --wb-text-secondary: #000000;
  --wb-text-muted: #000000;
  --wb-input-bg: #f1f5f9;
  --wb-input-border: rgba(15, 23, 42, 0.12);
  --wb-hover-bg: #f1f5f9;
  --wb-card-bg: #f8fafc;
  --wb-card-border: rgba(15, 23, 42, 0.1);
  --wb-save-border: rgba(15, 23, 42, 0.08);
  --wb-code-border: rgba(15, 23, 42, 0.1);
  --wb-hint: #000000;
}

/* Dark mode: driven by JS MutationObserver → .is-dark on the root element */
.widget-builder-page.is-dark {
  /* Force the builder UI to stay light (LiveChat-like) even in dashboard dark mode. */
  --wb-bg: #ffffff;
  --wb-panel: #ffffff;
  --wb-border: rgba(15, 23, 42, 0.1);
  --wb-section-bg: #ffffff;
  --wb-section-border: rgba(15, 23, 42, 0.08);
  --wb-text-primary: #0f172a;
  --wb-text-secondary: #475569;
  --wb-text-muted: #94a3b8;
  --wb-input-bg: #f1f5f9;
  --wb-input-border: rgba(15, 23, 42, 0.12);
  --wb-hover-bg: #f1f5f9;
  --wb-card-bg: #f8fafc;
  --wb-card-border: rgba(15, 23, 42, 0.1);
  --wb-save-border: rgba(15, 23, 42, 0.08);
  --wb-code-border: rgba(15, 23, 42, 0.1);
  --wb-hint: #64748b;
}

.widget-builder-page {
  padding: 16px 20px 20px;
  height: 100%;
  background: var(--wb-bg);
}

.widget-builder-page input[type='radio'],
.widget-builder-page input[type='checkbox'] {
  accent-color: #3b82f6;
}

.widget-builder-layout {
  display: grid;
  grid-template-columns: minmax(460px, 58%) minmax(360px, 42%);
  gap: 16px;
  height: 100%;
  min-height: 700px;
}

/* ── Config Panel ── */
.builder-config-panel {
  border: 1px solid var(--wb-border);
  border-radius: 12px;
  background: var(--wb-panel);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.builder-header {
  padding: 18px 18px 10px;
}

.builder-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--wb-text-primary);
}

.builder-subtitle {
  margin: 8px 0 0;
  color: var(--wb-text-muted);
  font-size: 12px;
}

.builder-sections {
  padding: 8px 14px 14px;
  overflow-y: auto;
  flex: 1;
}

/* ── Accordion sections ── */
.builder-section {
  border: 1px solid var(--wb-section-border);
  border-radius: 10px;
  background: var(--wb-section-bg);
  overflow: hidden;
  margin-bottom: 10px;
}

.section-trigger {
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: var(--wb-text-primary);
}

.section-trigger:hover {
  background: var(--wb-hover-bg);
}

.section-left {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.section-icon {
  font-size: 15px;
  color: var(--wb-text-secondary);
}

.section-name {
  font-size: 14px;
  color: var(--wb-text-primary);
  font-weight: 500;
}

.section-badge {
  font-size: 10px;
  font-weight: 700;
  color: #2563eb;
  background: rgba(37, 99, 235, 0.12);
  padding: 2px 6px;
  border-radius: 999px;
}

.section-chevron {
  color: var(--wb-text-muted);
  font-size: 15px;
}

.section-content {
  border-top: 1px solid var(--wb-section-border);
  padding: 12px 14px 14px;
}

.section-help {
  margin: 0 0 10px;
  font-size: 12px;
  color: var(--wb-text-muted);
}

/* ── Script box ── */
.script-box {
  border: 1px solid var(--wb-code-border);
  border-radius: 8px;
  overflow: hidden;
}

.script-box :deep(code.hljs) {
  background: transparent;
  color: var(--wb-text-primary);
}

.script-box :deep(.hljs-title),
.script-box :deep(.hljs-section) {
  color: inherit !important;
}

.section-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}

.ghost-btn {
  border: 1px solid var(--wb-input-border);
  border-radius: 8px;
  background: transparent;
  color: var(--wb-text-primary);
  font-size: 12px;
  padding: 7px 10px;
  cursor: pointer;
}

.ghost-btn:hover {
  background: var(--wb-hover-bg);
}

/* ── Option blocks ── */
.option-block {
  margin-top: 8px;
}

.option-title {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--wb-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.option-hint {
  margin: 0 0 10px;
  font-size: 11px;
  color: var(--wb-hint);
  line-height: 1.5;
}

.option-label {
  margin: 10px 0 8px;
  font-size: 12px;
  color: var(--wb-text-secondary);
  font-weight: 500;
}

.option-grid {
  display: grid;
  gap: 10px;
}

.option-grid.two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

/* ── Midnight template toggle ── */
/* ── Choice cards ── */
.choice-card {
  border: 1px solid var(--wb-card-border);
  border-radius: 10px;
  background: var(--wb-card-bg) !important;
  height: 88px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--wb-text-secondary);
  font-size: 12px;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    color 0.15s ease;
}

.choice-card:hover {
  border-color: rgba(59, 130, 246, 0.5);
  color: var(--wb-text-primary);
}

.choice-card.active {
  border-color: #3b82f6;
  color: #3b82f6;
}

.choice-icon {
  width: 34px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid currentColor;
  display: inline-block;
}

.choice-icon-bar {
  border-radius: 4px;
  height: 16px;
}

.choice-icon-bubble {
  width: 22px;
  height: 22px;
  border-radius: 999px;
}

.choice-lucide {
  font-size: 18px;
}

/* ── Swatches ── */
.swatch-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.swatch-dot {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 2px solid transparent;
  cursor: pointer;
  transition:
    transform 0.1s ease,
    box-shadow 0.1s ease;
}

.swatch-dot:hover {
  transform: scale(1.15);
}

.swatch-dot.active {
  border-color: #fff;
  box-shadow: 0 0 0 2px #3b82f6;
}

/* ── Position controls ── */
.inline-controls {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field span {
  font-size: 12px;
  color: var(--wb-text-secondary);
  font-weight: 500;
}

.field input,
.field select {
  height: 32px;
  border: 1px solid var(--wb-input-border);
  border-radius: 8px;
  background: var(--wb-input-bg);
  color: var(--wb-text-primary);
  font-size: 12px;
  padding: 0 8px;
  outline: none;
  transition: border-color 0.15s;
}

.field input:focus,
.field select:focus {
  border-color: #3b82f6;
}

/* ── Toggle rows ── */
.radio-line,
.switch-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0;
  font-size: 13px;
  color: var(--wb-text-primary);
  cursor: pointer;
}

.radio-line.disabled,
.switch-row.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tweak-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 8px 0;
}

.upload-link {
  position: relative;
  font-size: 12px;
  color: #3b82f6;
  cursor: pointer;
}

.upload-link:hover {
  text-decoration: underline;
}

.hidden-upload {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}

/* ── Save row ── */
.save-row {
  border-top: 1px solid var(--wb-save-border);
  padding: 12px 14px;
}

/* ── Preview Panel ── */
.builder-preview-panel {
  border: 1px solid var(--wb-border);
  border-radius: 12px;
  background: var(--wb-panel);
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.preview-header {
  padding: 14px 16px;
  border-bottom: 1px solid var(--wb-section-border);
  font-size: 16px;
  font-weight: 600;
  color: var(--wb-text-primary);
}

.preview-stage {
  flex: 1;
  min-height: 520px;
  margin: 12px;
  border-radius: 10px;
  border: 1px solid var(--wb-code-border);
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
}

.preview-device-shell {
  width: min(356px, 100%);
  height: 640px;
  border-radius: 18px;
  border: 1px solid var(--wb-code-border);
  overflow: hidden;
  background: #dfe3e8;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.22);
}

.sdk-preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  display: block;
}

.preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: var(--wb-text-muted);
  font-size: 13px;
}

.preview-note {
  margin: 0 12px 6px;
  font-size: 11px;
  color: var(--wb-text-muted);
}

.chat-page-link {
  display: block;
  margin: 0 auto 14px;
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 13px;
  cursor: pointer;
  text-align: center;
}

.chat-page-link:hover {
  text-decoration: underline;
}

@media (max-width: 1360px) {
  .widget-builder-layout {
    grid-template-columns: 1fr;
  }
}

/* ──────────────────────────────────────────────────────────────
   LiveChat 1:1 visual overrides (ref: /root/livechatsetting)
   These rules intentionally come last to override earlier styles.
   ────────────────────────────────────────────────────────────── */
.widget-builder-page {
  --lc-accent: rgb(67, 132, 245);
  --lc-surface: #ffffff;
  --lc-surface-2: #f6f6f7;
  --lc-border: rgba(66, 77, 87, 0.16);
  --lc-border-strong: rgb(188, 198, 208);
  --lc-text: #000000;
  --lc-text-80: #000000;
  --lc-text-60: #000000;
  --lc-text-50: #000000;

  padding: 0 !important;
  background: transparent !important;
  height: 100vh !important;
  overflow: hidden !important;
}

.widget-builder-page.is-dark {
  /* Keep the settings builder content in a consistent white theme. */
  --lc-surface: #ffffff;
  --lc-surface-2: #ffffff;
  --lc-border: rgba(66, 77, 87, 0.16);
  --lc-border-strong: rgb(188, 198, 208);
  --lc-text: #000000;
  --lc-text-80: #000000;
  --lc-text-60: #000000;
  --lc-text-50: #000000;
}

.widget-builder-layout {
  display: flex !important;
  gap: 0 !important;
  grid-template-columns: none !important;
  min-height: 0 !important;
  height: 100% !important;
  overflow: hidden !important;
  align-items: stretch;
}

/* Left column – independent scroll (header fixed, sections scroll, save bar fixed) */
.builder-config-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  border: none !important;
  border-radius: 0 !important;
  background: transparent !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
}

.builder-header {
  flex-shrink: 0 !important;
  display: flex !important;
  align-items: center !important;
  height: 56px !important;
  padding: 0 !important;
  border-bottom: 1px solid #eeeeef !important;
  background: #ffffff !important;
}

.builder-title {
  margin: 0 !important;
  height: 56px !important;
  line-height: 56px !important;
  padding-left: 21px !important;
  letter-spacing: 0.3px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #111827 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  text-align: left !important;
}

.builder-subtitle {
  margin: 0 0 20px !important;
  padding: 0 !important;
  font-size: 14px !important;
  line-height: 22px !important;
  color: #6b7280 !important;
}

.builder-sections {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  max-width: 960px;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  overscroll-behavior: contain !important;
  padding: 32px 32px 120px !important;
}

.save-row {
  flex-shrink: 0 !important;
  padding: 12px 32px !important;
  border-top: 1px solid var(--lc-border) !important;
  background: var(--lc-surface) !important;
}

/* Accordion cards */
.builder-section {
  border: solid 1px var(--lc-border) !important;
  margin-bottom: 12px !important;
  border-radius: 4px !important;
  background: var(--lc-surface) !important;
  width: 100%;
  max-width: 705px;
  overflow: hidden;
}

.section-trigger {
  min-height: 60px !important;
  padding: 0 16px !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  color: var(--lc-text) !important;
  background: transparent !important;
}

.section-trigger:hover {
  background: rgba(0, 0, 0, 0.03) !important;
}

.widget-builder-page.is-dark .section-trigger:hover {
  background: rgba(0, 0, 0, 0.03) !important;
}

.section-icon {
  color: var(--lc-text-60) !important;
}

.section-name {
  font-size: 13px !important;
  font-weight: 700 !important;
  color: var(--lc-text) !important;
}

.section-badge {
  font-size: 10px !important;
  font-weight: 700 !important;
  color: var(--lc-accent) !important;
  background: rgba(67, 132, 245, 0.12) !important;
  text-transform: uppercase;
}

.section-chevron {
  color: var(--lc-text-60) !important;
  transform: rotate(0deg);
  transition: transform 0.2s ease;
}

.section-trigger[aria-expanded='true'] .section-chevron {
  transform: rotate(90deg);
}

.section-content {
  border-top: 1px solid var(--lc-border) !important;
  padding: 16px !important;
}

.section-help,
.option-hint {
  color: var(--lc-text-60) !important;
  font-size: 11px !important;
  line-height: 20px !important;
}

.option-title {
  font-size: 11px !important;
  text-transform: uppercase;
  color: var(--lc-text) !important;
  opacity: 0.6;
}

.option-grid.two {
  display: flex !important;
  flex-wrap: wrap;
  gap: 16px !important;
}

/* Choice cards */
.choice-card {
  position: relative !important;
  display: inline-block !important;
  width: 96px !important;
  height: 104px !important;
  padding-top: 10px !important;
  border-radius: 8px !important;
  background: transparent !important;
  border: none !important;
  text-align: center;
  cursor: pointer;
}

/* Override legacy dark-mode rules that set card backgrounds */
.widget-builder-page.is-dark .choice-card,
.widget-builder-page.is-dark .choice-card:hover,
.widget-builder-page.is-dark .choice-card.active {
  background: transparent !important;
  border: none !important;
  border-color: transparent !important;
  color: inherit !important;
}

.choice-box {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  margin-left: auto;
  margin-right: auto;
  width: 72px;
  height: 72px;
  border-radius: 6px;
  background-color: var(--lc-surface);
  border: 2px solid var(--lc-border-strong);
  color: var(--lc-text);
}

.choice-box.active {
  border-color: var(--lc-accent);
}

.choice-svg {
  display: flex;
  align-items: center;
  justify-content: center;
}

.theme-mini {
  width: 34px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #ffffff;
  position: relative;
  overflow: hidden;
}

.theme-mini::after {
  content: '';
  position: absolute;
  left: 6px;
  right: 6px;
  bottom: 5px;
  height: 6px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.08);
}

.theme-mini--classic::before {
  content: '';
  position: absolute;
  left: 6px;
  top: 5px;
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: var(--lc-accent);
}

.theme-mini--midnight {
  background: #111111;
  border-color: rgba(255, 255, 255, 0.14);
}

.theme-mini--midnight::after {
  background: rgba(255, 255, 255, 0.12);
}

.theme-mini--midnight::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.12) 0%,
    rgba(255, 255, 255, 0) 60%
  );
}

.choice-svg.subtle {
  color: rgba(66, 77, 87, 0.6);
}

.widget-builder-page.is-dark .choice-svg.subtle {
  color: var(--lc-text-60);
}

.choice-check {
  position: absolute;
  display: none;
  top: -11px;
  right: -11px;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background-color: var(--lc-accent);
  justify-content: center;
  align-items: center;
  color: #fff;
}

.choice-check.show {
  display: flex;
}

.choice-text {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: var(--lc-text-50);
}

/* Color mode radios (Theme color / More color settings) */
.color-mode-list {
  margin: 20px 0 0;
}

.color-mode {
  display: inline-flex;
  width: 100%;
  margin-bottom: 20px;
  cursor: auto;
  align-items: flex-start;
}

.color-mode .circle {
  position: relative;
  width: 16px;
  height: 16px;
  border-radius: 16px;
  background-color: rgb(243, 247, 249);
  border: 1px solid var(--lc-border-strong);
  margin-right: 10px;
  transition: 0.2s;
  box-sizing: border-box;
  margin-top: 1px;
}

.widget-builder-page.is-dark .color-mode .circle {
  background-color: rgb(243, 247, 249);
}

.color-mode.selected .circle {
  border-color: var(--lc-accent);
}

.color-mode .inner-circle {
  opacity: 0;
  position: absolute;
  top: 4px;
  left: 4px;
  width: 6px;
  height: 6px;
  border-radius: 6px;
  background-color: var(--lc-accent);
  transition: inherit;
}

.color-mode.selected .inner-circle {
  opacity: 1;
}

.color-mode .circle input {
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.color-mode .content {
  flex: 1;
}

.color-mode-title {
  color: var(--lc-text);
  margin-top: 1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
}

/* Swatches */
.swatch-row {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
  min-height: 50px;
  margin: 0 0 25px 26px !important;
  gap: 0 !important;
}

.swatch-dot {
  position: relative;
  width: 30px !important;
  height: 30px !important;
  border-radius: 50% !important;
  margin-right: 8px !important;
  border: 0 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0 !important;
}

.swatch-dot.active {
  outline: 2px solid var(--lc-accent);
  outline-offset: 2px;
  border-color: transparent !important;
}

.swatch-hit {
  position: absolute;
  inset: 0;
  opacity: 0;
}

/* Advanced color fields */
.advanced-colors {
  margin: 20px 0 0;
}

/* Midnight bubble gradient presets */
.midnight-bubble-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 4px;
}

.mn-preset-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition:
    transform 120ms ease,
    border-color 120ms ease;
  outline: none;
  flex-shrink: 0;
}

.mn-preset-btn:hover {
  transform: scale(1.12);
}

.mn-preset-btn.active {
  border-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.4);
  transform: scale(1.08);
}

.announcement-panel {
  margin-top: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #f6f6f7;
  border-radius: 12px;
  padding: 12px;
}

.announcement-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.announcement-field.full {
  grid-column: 1 / -1;
}

.announcement-field label {
  font-size: 12px;
  line-height: 20px;
  color: var(--lc-text-80);
  display: block;
}

/* Make announcement inputs look like LiveChat (no black background) */
.manual-input {
  height: 38px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #ffffff;
  color: #000000;
  padding: 0 12px;
  width: 100%;
  box-sizing: border-box;
  outline: none;
}

.manual-input:focus {
  border-color: rgba(59, 130, 246, 0.55);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
}

.manual-input--wide {
  width: 100%;
}

.advanced-hint {
  margin: 0 0 16px;
  font-size: 12px;
  line-height: 20px;
  color: var(--lc-text-60);
}

.advanced-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--lc-text);
  margin: 12px 0 10px;
}

.widget-color-popover {
  position: fixed;
  z-index: 10000;
}

.widget-color-popover__chrome.vc-chrome {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.24);
}

.color-grid {
  display: flex;
  flex-flow: wrap;
  max-width: 520px;
  margin-bottom: 20px;
}

.color-field {
  flex: 0 1 auto;
  max-width: 240px;
  margin-right: 20px;
  margin-bottom: 20px;
  min-width: 149px;
}

.color-field label {
  font-size: 12px;
  line-height: 20px;
  color: var(--lc-text-80);
  display: block;
  cursor: default;
}

.color-input {
  display: flex;
  flex-direction: row;
  margin-top: 4px;
}

.color-input .manual-input,
.opacity-input {
  width: 81px;
  height: 38px;
  margin: 0;
  font-size: 12px;
  line-height: 20px;
  padding: 0 10px;
  color: var(--lc-text);
  border: 1px solid var(--lc-border);
  background-color: #ffffff;
  box-sizing: border-box;
  outline: none;
}

.color-input .manual-input {
  border-radius: 6px 0 0 6px;
}

.opacity-input {
  border-radius: 6px;
  margin-top: 4px;
}

.color-swatch-btn {
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 0 10px;
  border-radius: 0 6px 6px 0;
  border: 1px solid var(--lc-border);
  border-left: none;
  background: transparent;
  color: var(--lc-text-60);
}

.swatch-square {
  width: 22px;
  height: 22px;
  border: 1px solid var(--lc-border);
  border-radius: 3px;
  margin-right: 8px;
  box-sizing: border-box;
}

/* Inputs (Position) */
.field {
  color: var(--lc-text-80) !important;
  font-size: 12px !important;
}

.field input,
.field select {
  height: 38px !important;
  border-radius: 6px !important;
  border: 1px solid var(--lc-border) !important;
  background: transparent !important;
  color: var(--lc-text) !important;
}

.widget-builder-page.is-dark .field input,
.widget-builder-page.is-dark .field select {
  background: transparent !important;
  color: var(--lc-text) !important;
  border-color: var(--lc-border) !important;
}

.ghost-btn {
  border: 1px solid var(--lc-border) !important;
  border-radius: 6px !important;
  background: transparent !important;
  color: var(--lc-text) !important;
  font-size: 12px !important;
  height: 38px !important;
  padding: 0 12px !important;
}

.widget-builder-page.is-dark .ghost-btn {
  background: transparent !important;
  border-color: var(--lc-border) !important;
  color: var(--lc-text) !important;
}

/* Save/Update button – force LiveChat-like blue (avoid DS defaults) */
.save-row :deep(.lc-save-btn) {
  height: 38px !important;
  padding: 0 16px !important;
  border-radius: 6px !important;
  background: var(--lc-accent) !important;
  border: 1px solid var(--lc-accent) !important;
  color: #fff !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.save-row :deep(.lc-save-btn:hover:enabled),
.save-row :deep(.lc-save-btn:focus-visible:enabled) {
  background: #3b78e0 !important;
  border-color: #3b78e0 !important;
}

/* Preview column – fixed on screen */
.builder-preview-panel {
  flex: 0 0 37.5% !important;
  min-width: 0;
  border: none !important;
  border-radius: 0 !important;
  background: #ffffff !important;
  border-left: 1px solid var(--lc-border) !important;
  height: 100% !important;
  max-height: 100% !important;
  overflow: hidden !important;
}

.preview-header {
  height: 56px !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  background: #ffffff !important;
  border-bottom: 1px solid var(--lc-border) !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  color: var(--lc-text) !important;
}

.preview-header::before {
  content: '';
  width: 20px;
}

.preview-stage {
  flex: 1;
  min-height: 0 !important;
  margin: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  background: #f8fafc !important;
  overflow: hidden;
  padding: 0 !important;
  align-items: center;
  justify-content: center;
}

.preview-device-shell {
  width: 320px !important;
  height: calc(100vh - 120px) !important;
  max-height: 560px !important;
  min-height: 480px !important;
  border-radius: 18px !important;
  border: 1px solid rgba(148, 163, 184, 0.45) !important;
  background: #ffffff !important;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14) !important;
}

.widget-builder-page.is-dark .preview-device-shell {
  border-color: rgba(148, 163, 184, 0.45) !important;
  background: #ffffff !important;
}

.chat-page-link {
  margin: 6px auto 14px !important;
  color: var(--lc-accent) !important;
}

@media (max-width: 1240px) {
  .widget-builder-layout {
    flex-direction: column !important;
  }

  .builder-preview-panel {
    border-left: none !important;
    border-top: 1px solid var(--lc-border) !important;
  }
}

/* ------------------------------------------------------------------
   Layout-only normalization (matches Language/Pre-chat shell)
   ------------------------------------------------------------------ */
.widget-builder-page {
  flex: 1;
  min-height: 0;
  height: 100% !important;
  width: 100%;
  padding: 0 !important;
  overflow: hidden !important;
  background: #ffffff !important;
  display: flex;
  flex-direction: column;
}

.widget-builder-layout {
  display: flex !important;
  flex: 1;
  min-height: 0 !important;
  height: 100% !important;
  overflow: hidden !important;
  gap: 0 !important;
}

.builder-config-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  min-height: 0;
  border: none !important;
  border-right: 1px solid #eeeeef !important;
  border-radius: 0 !important;
  background: #ffffff !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  height: 100% !important;
}

.builder-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  width: 100%;
  height: 56px;
  padding: 0 !important;
  border-bottom: 1px solid #eeeeef;
  background: #ffffff !important;
}

.builder-title {
  margin: 0 !important;
  height: 56px !important;
  line-height: 56px !important;
  padding-left: 21px;
  letter-spacing: 0.3px;
  font-size: 18px !important;
  font-weight: 600 !important;
  color: #111827 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

.builder-subtitle {
  margin: 0 0 20px !important;
  padding: 0 !important;
  font-size: 14px !important;
  line-height: 22px !important;
  color: #6b7280 !important;
}

.builder-sections {
  flex: 1;
  min-height: 0;
  max-width: 960px;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  overscroll-behavior: contain;
  padding: 32px 32px 120px !important;
}

.save-row {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 32px !important;
  border-top: 1px solid #eeeeef !important;
  background: #ffffff !important;
}

.save-row :deep(.lc-save-btn) {
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  background: #0059e1 !important;
  border: 1px solid #0059e1 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.save-row :deep(.lc-save-btn:hover:enabled),
.save-row :deep(.lc-save-btn:focus-visible:enabled) {
  background: #0047c2 !important;
  border-color: #0047c2 !important;
}

/* ── Preview panel (right column) — mirrors PoweredBy's proven structure ── */
.builder-preview-panel {
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  border: none !important;
  border-left: 1px solid #eeeeef !important;
  border-radius: 0 !important;
  min-width: 0;
  flex: 3 1 37.5% !important;
  z-index: 1;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
  background: #f5f5f5 !important;
}

.preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.preview-header-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  margin-left: 20px;
}

.preview-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  background: #f5f5f5;
}

.preview-scroll-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

.preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

.preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

.sdk-preview-frame {
  width: 360px !important;
  height: 682px !important;
  border: none !important;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
  background: transparent;
}

.preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

.preview-note {
  display: block !important;
  margin: 8px 0 4px !important;
  padding: 0 !important;
  font-size: 13px !important;
  color: #6b7280 !important;
  text-align: center;
}

@media (max-width: 1240px) {
  .widget-builder-layout {
    flex-direction: column !important;
  }

  .builder-config-panel {
    border-right: none !important;
    border-bottom: 1px solid #eeeeef !important;
    flex: 1 1 auto !important;
  }

  .builder-preview-panel {
    border-left: none !important;
    flex: 1 1 auto !important;
  }
}

/* ── Final definitive layout override for independent scroll (highest specificity) ── */
.widget-builder-page.wb-scroll-layout {
  height: 100% !important;
  min-height: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

.widget-builder-page.wb-scroll-layout > .widget-builder-layout {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
  display: flex !important;
}

/* Left panel: header(fixed) + sections(scroll) + save-row(fixed) */
.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel {
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
  flex: 5 1 62.5% !important;
}

.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel
  > .builder-header {
  flex-shrink: 0 !important;
  height: 56px !important;
  min-height: 56px !important;
}

.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel
  > .builder-sections {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  overscroll-behavior: contain !important;
}

.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-config-panel
  > .save-row {
  flex-shrink: 0 !important;
}

/* Right panel: header-bar(fixed) + content-area(scroll) */
.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-preview-panel {
  display: flex !important;
  flex-direction: column !important;
  min-height: 0 !important;
  overflow: hidden !important;
  flex: 3 1 37.5% !important;
}

.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-preview-panel
  > .preview-header-bar {
  flex-shrink: 0 !important;
  height: 56px !important;
  min-height: 56px !important;
}

.widget-builder-page.wb-scroll-layout
  > .widget-builder-layout
  > .builder-preview-panel
  > .preview-content-area {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  overscroll-behavior: contain !important;
}
</style>
